import { Field, SaveBar, useDraft } from "@/components/hq/useDraft";
import { ImageField } from "@/components/hq/ImageField";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Trash2 } from "lucide-react";

const inputCls = "rounded-sm bg-[#0A0A0A]";
const monoCls = "mono rounded-sm bg-[#0A0A0A] text-sm";

const Row = ({ children, testId }) => (
  <div className="border border-border bg-[#141414] rounded-sm p-4" data-testid={testId}>
    {children}
  </div>
);

export const RecipeEditor = () => {
  const { draft, update, save, dirty } = useDraft("recipes");
  if (!draft) return null;
  const set = (id, patch) => update(draft.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  return (
    <div data-testid="hq-recipes">
      <SaveBar
        dirty={dirty}
        save={save}
        testId="recipes"
        addLabel="+ Recipe"
        onAdd={() =>
          update([
            ...draft,
            { id: `rec-${Date.now()}`, name: "New recipe", category: "Specialty", cupSize: "Standard Smash cup", ingredients: "", procedure: "", notes: "", image: "" },
          ])
        }
      />
      <div className="space-y-4">
        {draft.map((r) => (
          <Row key={r.id} testId={`hq-recipe-${r.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              {r.image ? (
                <img src={r.image} alt="" className="h-12 w-12 shrink-0 object-contain" />
              ) : null}
              <Input
                data-testid={`hq-recipe-name-${r.id}`}
                value={r.name}
                onChange={(e) => set(r.id, { name: e.target.value })}
                className={`${inputCls} display text-xl uppercase md:max-w-sm`}
              />
              <Input value={r.category || ""} onChange={(e) => set(r.id, { category: e.target.value })} placeholder="Category" className={`${inputCls} md:w-40`} />
              <Input value={r.cupSize || ""} onChange={(e) => set(r.id, { cupSize: e.target.value })} placeholder="Cup / box size" className={`${inputCls} md:w-56`} />
              <button onClick={() => update(draft.filter((x) => x.id !== r.id))} className="ml-auto text-muted-foreground hover:text-destructive">
                <Trash2 size={16} />
              </button>
            </div>
            <Field label="CUP PHOTO URL">
              <ImageField label="" testId={`hq-recipe-image-${r.id}`} preview={false}
                value={r.image || ""} onChange={(url) => set(r.id, { image: url })} />
            </Field>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <Field label="INGREDIENTS + PORTIONS">
                <Textarea data-testid={`hq-recipe-ingredients-${r.id}`} value={r.ingredients} onChange={(e) => set(r.id, { ingredients: e.target.value })} className={monoCls} rows={5} />
              </Field>
              <Field label="PROCEDURE">
                <Textarea value={r.procedure} onChange={(e) => set(r.id, { procedure: e.target.value })} className={monoCls} rows={5} />
              </Field>
            </div>
            <Field label="NOTES (OPERATOR-VISIBLE)">
              <Input value={r.notes || ""} onChange={(e) => set(r.id, { notes: e.target.value })} className={monoCls} />
            </Field>
          </Row>
        ))}
      </div>
    </div>
  );
};

export const ResourceEditor = () => {
  const { draft, update, save, dirty } = useDraft("resources");
  if (!draft) return null;
  const set = (id, patch) => update(draft.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  return (
    <div data-testid="hq-resources">
      <SaveBar
        dirty={dirty}
        save={save}
        testId="resources"
        addLabel="+ Resource"
        onAdd={() =>
          update([...draft, { id: `res-${Date.now()}`, title: "New resource", type: "Document", category: "Operations", url: "", body: "" }])
        }
      />
      <div className="space-y-4">
        {draft.map((r) => (
          <Row key={r.id} testId={`hq-resource-${r.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <Input data-testid={`hq-resource-title-${r.id}`} value={r.title} onChange={(e) => set(r.id, { title: e.target.value })} className={`${inputCls} md:max-w-sm`} />
              <Input value={r.type || ""} onChange={(e) => set(r.id, { type: e.target.value })} placeholder="Type" className={`${inputCls} md:w-40`} />
              <Input value={r.category || ""} onChange={(e) => set(r.id, { category: e.target.value })} placeholder="Category" className={`${inputCls} md:w-44`} />
              <button onClick={() => update(draft.filter((x) => x.id !== r.id))} className="ml-auto text-muted-foreground hover:text-destructive">
                <Trash2 size={16} />
              </button>
            </div>
            <div className="mt-3 grid gap-3">
              <Field label="LINK URL">
                <Input value={r.url || ""} onChange={(e) => set(r.id, { url: e.target.value })} className={monoCls} />
              </Field>
              <Field label="BODY / CONTENT">
                <Textarea data-testid={`hq-resource-body-${r.id}`} value={r.body || ""} onChange={(e) => set(r.id, { body: e.target.value })} className={monoCls} rows={4} />
              </Field>
            </div>
          </Row>
        ))}
      </div>
    </div>
  );
};

export const HelpEditor = () => {
  const { draft, update, save, dirty } = useDraft("help");
  if (!draft) return null;
  const set = (id, patch) => update(draft.map((h) => (h.id === id ? { ...h, ...patch } : h)));
  return (
    <div data-testid="hq-help">
      <SaveBar
        dirty={dirty}
        save={save}
        testId="help"
        addLabel="+ Topic"
        onAdd={() => update([...draft, { id: `h-${Date.now()}`, topic: "New topic", answer: "" }])}
      />
      <div className="space-y-4">
        {draft.map((h) => (
          <Row key={h.id} testId={`hq-help-${h.id}`}>
            <div className="flex items-center gap-3">
              <Input data-testid={`hq-help-topic-${h.id}`} value={h.topic} onChange={(e) => set(h.id, { topic: e.target.value })} className={inputCls} />
              <button onClick={() => update(draft.filter((x) => x.id !== h.id))} className="text-muted-foreground hover:text-destructive">
                <Trash2 size={16} />
              </button>
            </div>
            <Field label="ANSWER">
              <Textarea data-testid={`hq-help-answer-${h.id}`} value={h.answer || ""} onChange={(e) => set(h.id, { answer: e.target.value })} className={monoCls} rows={3} />
            </Field>
          </Row>
        ))}
      </div>
    </div>
  );
};

export const ChecklistEditor = () => {
  const { draft, update, save, dirty } = useDraft("checklist");
  if (!draft) return null;
  const set = (id, patch) => update(draft.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  return (
    <div data-testid="hq-checklist">
      <SaveBar
        dirty={dirty}
        save={save}
        testId="checklist"
        addLabel="+ Item"
        onAdd={() => update([...draft, { id: `c-${Date.now()}`, stage: "Event day", item: "New checklist item" }])}
      />
      <div className="space-y-2">
        {draft.map((c) => (
          <div key={c.id} className="flex flex-wrap items-center gap-3 border border-border bg-[#141414] rounded-sm p-3" data-testid={`hq-checklist-${c.id}`}>
            <Input value={c.stage} onChange={(e) => set(c.id, { stage: e.target.value })} className={`${inputCls} md:w-44`} placeholder="Stage" />
            <Input data-testid={`hq-checklist-item-${c.id}`} value={c.item} onChange={(e) => set(c.id, { item: e.target.value })} className={`${inputCls} flex-1`} />
            <button onClick={() => update(draft.filter((x) => x.id !== c.id))} className="text-muted-foreground hover:text-destructive">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const MerchEditor = () => {
  const { draft, update, save, dirty } = useDraft("merch");
  if (!draft) return null;
  const set = (id, patch) => update(draft.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  return (
    <div data-testid="hq-merch">
      <SaveBar
        dirty={dirty}
        save={save}
        testId="merch"
        addLabel="+ Item"
        onAdd={() =>
          update([
            ...draft,
            { id: `mer-${Date.now()}`, name: "New item", price: "", category: "Uniform", image: "", url: "", note: "" },
          ])
        }
      />
      <div className="space-y-4">
        {draft.map((m) => (
          <Row key={m.id} testId={`hq-merch-${m.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              {m.image ? (
                <img src={m.image} alt="" className="h-12 w-12 rounded-sm object-cover" />
              ) : null}
              <Input
                data-testid={`hq-merch-name-${m.id}`}
                value={m.name}
                onChange={(e) => set(m.id, { name: e.target.value })}
                className={`${inputCls} display text-xl uppercase md:max-w-sm`}
              />
              <Input value={m.price || ""} onChange={(e) => set(m.id, { price: e.target.value })} placeholder="$0.00" className={`${monoCls} md:w-28`} />
              <Input value={m.category || ""} onChange={(e) => set(m.id, { category: e.target.value })} placeholder="Category" className={`${inputCls} md:w-40`} />
              <button onClick={() => update(draft.filter((x) => x.id !== m.id))} className="ml-auto text-muted-foreground hover:text-destructive">
                <Trash2 size={16} />
              </button>
            </div>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <Field label="IMAGE URL">
                <ImageField label="" testId={`hq-merch-image-${m.id}`} preview={false}
                  value={m.image || ""} onChange={(url) => set(m.id, { image: url })} />
              </Field>
              <Field label="PRODUCT URL">
                <Input value={m.url || ""} onChange={(e) => set(m.id, { url: e.target.value })} className={monoCls} />
              </Field>
            </div>
            <Field label="NOTE (OPERATOR-VISIBLE)">
              <Input value={m.note || ""} onChange={(e) => set(m.id, { note: e.target.value })} className={monoCls} />
            </Field>
          </Row>
        ))}
      </div>
    </div>
  );
};

export const CertificationEditor = () => {
  const { draft, update, save, dirty } = useDraft("certifications");
  if (!draft) return null;
  const setCriteria = (key, criteria) => update({ ...draft, [key]: { ...draft[key], criteria } });
  return (
    <div data-testid="hq-certifications">
      <SaveBar dirty={dirty} save={save} testId="certifications" />
      <div className="grid gap-4 md:grid-cols-2">
        {["product", "standard"].map((key) => (
          <Row key={key} testId={`hq-cert-${key}`}>
            <Input
              value={draft[key].title}
              onChange={(e) => update({ ...draft, [key]: { ...draft[key], title: e.target.value } })}
              className={`${inputCls} display text-xl uppercase`}
            />
            <div className="mt-3 space-y-2">
              {draft[key].criteria.map((c) => (
                <div key={c.id} className="flex items-center gap-2">
                  <Input
                    data-testid={`hq-cert-criterion-${c.id}`}
                    value={c.text}
                    onChange={(e) =>
                      setCriteria(
                        key,
                        draft[key].criteria.map((x) => (x.id === c.id ? { ...x, text: e.target.value } : x))
                      )
                    }
                    className={monoCls}
                  />
                  <button
                    onClick={() => setCriteria(key, draft[key].criteria.filter((x) => x.id !== c.id))}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
              <button
                data-testid={`hq-cert-add-${key}`}
                onClick={() => setCriteria(key, [...draft[key].criteria, { id: `${key}-${Date.now()}`, text: "New criterion" }])}
                className="mono text-[11px] tracking-widest text-primary hover:underline"
              >
                + ADD CRITERION
              </button>
            </div>
          </Row>
        ))}
      </div>
    </div>
  );
};
